import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import {
  QUERY_NOTIFICATION_SETTINGS,
  UPDATE_NOTIFICATION_SETTINGS,
} from "../../../graphql/notificationSetting";
import Role from "../../../enums/Role";
import { lowerCase, sortBy } from "lodash";
import { toast } from "react-toastify";
import NotificationChannelTable, {
  NotificationChannelRow,
  NotificationSettingInput,
} from "components/common/notifications/NotificationChannelTable";

interface NotificationSettingsTableProps {
  userRole: Role;
}

/**
 * A row as the schema actually declares it: only `id` is non-null, `userRole`
 * is a plain String rather than the Role enum, and the list permits null
 * elements. `useQuery` is untyped, so nothing else enforces this.
 */
interface NotificationSettingRow {
  id: string;
  userRole?: string | null;
  category?: string | null;
  isEnable?: boolean | null;
  deliveryModes?: (string | null)[] | null;
}

/** The same row once normalized, which is all the render path deals with. */
interface NotificationSetting {
  id: string;
  userRole: string;
  category: string;
  isEnable: boolean;
  deliveryModes: string[];
}

/**
 * The switch is controlled, so a null `isEnable` would flip it to uncontrolled
 * and React would warn. Normalizing once here keeps every consumer simple.
 */
const normalize = (row: NotificationSettingRow): NotificationSetting => ({
  id: row.id,
  userRole: row.userRole ?? "",
  category: row.category ?? "",
  isEnable: row.isEnable ?? false,
  deliveryModes: (row.deliveryModes ?? []).filter(
    (mode): mode is string => mode != null,
  ),
});

/** Managers and administrators read their own copy of each setting. */
const copySuffix = (userRole: string) =>
  userRole === Role.Admin || userRole === Role.Manager
    ? `_${lowerCase(userRole)}`
    : "";

const NotificationSettingsTable: React.FC<NotificationSettingsTableProps> = (
  props,
) => {
  const { t } = useTranslation();
  const { userRole } = props;

  const {
    data: settingData,
    loading: isLoadingSetting,
    refetch: reloadData,
  } = useQuery(QUERY_NOTIFICATION_SETTINGS);

  const [updateNotificationSetting] = useMutation(UPDATE_NOTIFICATION_SETTINGS);

  // Null elements are allowed by the schema, so they go before filtering.
  const rows: NotificationChannelRow[] = useMemo(() => {
    const settings = (
      (settingData?.notificationSettings ??
        []) as (NotificationSettingRow | null)[]
    )
      .filter((row): row is NotificationSettingRow => row != null)
      .map(normalize)
      .filter((setting) => setting.userRole === userRole);

    return sortBy(settings, ["category"]).map((setting) => {
      const suffix = copySuffix(setting.userRole);
      return {
        ...setting,
        title: t(`notificationSettings.${setting.category}_title${suffix}`),
        description: t(
          `notificationSettings.${setting.category}_description${suffix}`,
        ),
      };
    });
  }, [userRole, settingData, t]);

  const saveSetting = async (id: string, input: NotificationSettingInput) => {
    try {
      await updateNotificationSetting({ variables: { id, input } });
      toast.dismiss();
      toast.success(t("notificationSettings.updateNotificationSuccess"));
      reloadData();
    } catch (error) {
      toast.error((error as Error)?.message || t("Please try again!"));
    }
  };

  return (
    <NotificationChannelTable
      heading={t(userRole)}
      channelLabels={{
        email: t("notificationSettings.email"),
        sms: t("notificationSettings.sms"),
        push: t("notificationSettings.mobilePush"),
      }}
      rows={rows}
      loading={isLoadingSetting && !settingData}
      onSave={saveSetting}
    />
  );
};

export default NotificationSettingsTable;
